import React, { useState } from 'react';
import VoiceDictation from './VoiceDictation.jsx';
import styles from './AiReadPanel.module.css';

// States: 'none' | 'generating' | 'generated' | 'signed'
export default function AiReadPanel({
  aiRead,
  state,
  onGenerate,
  onSign,
  onDictate,
}) {
  const [editedFindings, setEditedFindings]         = useState(null);
  const [editedImpression, setEditedImpression]     = useState(null);
  const [editedRecommendations, setEditedRecs]      = useState(null);
  const [reviewed, setReviewed]                     = useState(false);

  const findings  = Array.isArray(aiRead?.findings)  ? aiRead.findings  : [];
  const recs      = Array.isArray(aiRead?.recommendations) ? aiRead.recommendations : [];
  const impression = aiRead?.impression || '';

  if (state === 'none' || !aiRead) {
    return (
      <div className={styles.panel}>
        <button className={styles.genBtn} onClick={onGenerate}>Generate AI Read</button>
        <div className={styles.sub}>Azure OpenAI · GPT-4o</div>
        {onDictate ? (
          <div className={styles.voiceBlock}>
            <div className={styles.sub}>Or dictate findings directly:</div>
            <VoiceDictation onResult={(text) => onDictate?.(text)} />
          </div>
        ) : null}
      </div>
    );
  }

  if (state === 'generating') {
    return (
      <div className={styles.panel}>
        <div className={styles.pulsing}>
          <div className={styles.pulse} />
          <div className={styles.pulseLabel}>Analyzing image…</div>
        </div>
      </div>
    );
  }

  const isSigned = state === 'signed' || aiRead.finalized;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        {isSigned ? (
          <>
            <span className={`${styles.badge} ${styles.badgeSigned}`}>SIGNED</span>
            <span className={styles.signedBy}>{aiRead.signed_by} · {aiRead.signed_at?.slice(0, 19).replace('T', ' ')}</span>
          </>
        ) : (
          <span className={styles.badge}>AI PRELIMINARY</span>
        )}
      </div>
      <div className={styles.notDiagnostic}>
        For viewing only — not for primary diagnosis. AI output is preliminary
        and must be reviewed by a qualified clinician before use.
      </div>

      {aiRead.studyType || aiRead.study_type ? (
        <div className={styles.studyType}>{aiRead.studyType || aiRead.study_type}</div>
      ) : null}

      <section className={styles.section}>
        <div className={styles.sectionLabel}>Findings</div>
        <ul className={styles.findings}>
          {findings.map((f, i) => (
            <li key={i}
                contentEditable={!isSigned}
                suppressContentEditableWarning
                onInput={(e) => {
                  const next = [...findings];
                  next[i] = e.currentTarget.textContent;
                  setEditedFindings(next);
                }}>
              {f}
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionLabel}>Impression</div>
        <div
          className={styles.impression}
          contentEditable={!isSigned}
          suppressContentEditableWarning
          onInput={(e) => setEditedImpression(e.currentTarget.textContent)}
        >
          {impression}
        </div>
      </section>

      {recs.length ? (
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Recommendations</div>
          <ul className={styles.findings}>
            {recs.map((r, i) => (
              <li key={i}
                  contentEditable={!isSigned}
                  suppressContentEditableWarning
                  onInput={(e) => {
                    const next = [...recs];
                    next[i] = e.currentTarget.textContent;
                    setEditedRecs(next);
                  }}>
                {r}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {aiRead.confidenceNote || aiRead.confidence_note ? (
        <div className={styles.confidence}>{aiRead.confidenceNote || aiRead.confidence_note}</div>
      ) : null}

      {!isSigned ? (
        <div className={styles.footer}>
          {onDictate ? (
            <div className={styles.voiceRow}>
              <VoiceDictation onResult={(text) => onDictate?.(text)} />
              <span className={styles.voiceHint}>Dictate to append to impression</span>
            </div>
          ) : null}
          <label className={styles.checkbox}>
            <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
            <span>I have reviewed all findings</span>
          </label>
          <button
            className={styles.signBtn}
            disabled={!reviewed}
            onClick={() => onSign?.({
              editedFindings: editedFindings ?? undefined,
              editedImpression: editedImpression ?? undefined,
              editedRecommendations: editedRecommendations ?? undefined,
            })}
          >
            Sign &amp; Finalize
          </button>
        </div>
      ) : null}
    </div>
  );
}
